import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Layers, Clock, Activity, Eye } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, fmtDate, fmtDuration } from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { SyncDetailDialog } from "@/components/SyncDetailDialog";
import { useSyncStatus } from "@/components/SyncNowButton";

const Count = ({ n, tone }) => <span className={n ? tone : "text-slate-600"}>{n}</span>;

export default function SyncReports() {
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const { data: runs = [], isLoading } = useQuery({ queryKey: ["sync-runs"], queryFn: () => api.get("/admin/sync-runs").then((r) => r.data) });
  const { data: health } = useQuery({ queryKey: ["health"], queryFn: () => api.get("/health").then((r) => r.data) });
  const { data: status } = useSyncStatus();

  const last = runs[0];
  const okRate = runs.length ? Math.round((runs.filter((r) => r.status === "success").length / runs.length) * 100) : null;
  const rows = filter === "all" ? runs : runs.filter((r) => r.status === filter);

  return (
    <div className="space-y-8">
      <div className="rise">
        <p className="font-mono text-xs uppercase tracking-wider text-indigo-400">Nightly 02:00 IST · S3 → Index</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Sync Reports</h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base">What was added, updated, removed or failed in every S3 sync.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-total-documents" label="Documents" icon={FileText} value={health?.indexed_documents ?? "—"} sub="indexed files" />
        <StatCard testId="stat-total-chunks" label="Chunks" icon={Layers} value={health?.indexed_chunks?.toLocaleString() ?? "—"} sub="searchable pieces" delay={60} />
        <StatCard testId="stat-last-sync-status" label="Last sync" icon={Clock} delay={120}
          value={status?.running ? "Running" : last ? last.status : "—"}
          tone={status?.running ? "text-indigo-300" : last?.status === "success" ? "text-emerald-400" : last ? "text-amber-400" : "text-slate-50"}
          sub={last ? `${fmtDate(last.started_at)} · ${last.trigger} · ${fmtDuration(last.duration_seconds)}` : "no runs yet"} />
        <StatCard testId="stat-health-index" label="Success rate" icon={Activity} value={okRate == null ? "—" : `${okRate}%`} sub={`${runs.length} runs recorded`} delay={180} />
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-xl overflow-hidden rise" style={{ animationDelay: "200ms" }}>
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h2 className="font-heading text-lg font-semibold">Run history</h2>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="sync-status-filter" className="w-36 h-9 bg-[#080C14] border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "success", "partial", "error"].map((v) => <SelectItem key={v} value={v} data-testid={`sync-status-filter-${v}`} className="capitalize">{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="sync-reports-table">
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {["Started", "Trigger", "Added", "Updated", "Removed", "Failed", "Unchanged", "Duration", "Status", ""].map((h) => (
                  <TableHead key={h} className="font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`sync-run-row-${r.id}`} className="border-slate-800 hover:bg-slate-800/40 transition-colors cursor-pointer" onClick={() => setOpenId(r.id)}>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(r.started_at)}</TableCell>
                  <TableCell><StatusBadge value={r.trigger} /></TableCell>
                  <TableCell className="font-mono"><Count n={r.counts.added} tone="text-emerald-400" /></TableCell>
                  <TableCell className="font-mono"><Count n={r.counts.updated} tone="text-sky-400" /></TableCell>
                  <TableCell className="font-mono"><Count n={r.counts.removed} tone="text-slate-300" /></TableCell>
                  <TableCell className="font-mono"><Count n={r.counts.failed} tone="text-red-400" /></TableCell>
                  <TableCell className="font-mono text-slate-500">{r.skipped}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-400 whitespace-nowrap">{fmtDuration(r.duration_seconds)}</TableCell>
                  <TableCell><StatusBadge value={r.status} /></TableCell>
                  <TableCell>
                    <Button data-testid="sync-report-row-detail-button" size="sm" variant="ghost" className="text-slate-400 hover:text-white hover:bg-slate-800"><Eye className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow><TableCell colSpan={10} data-testid="sync-reports-empty" className="text-center py-12 text-slate-500 text-sm">
                  {isLoading ? "Loading…" : "No sync runs yet. Press “Sync now” or wait for the nightly run."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <SyncDetailDialog runId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
